import { parsePhoneNumber } from 'react-phone-number-input';

import { collectBrowserData } from './collectBrowserData';
import type { ThreeDsBrowserData } from './collectBrowserData';

export interface ThreeDsAddressPayload {
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface ThreeDsPayload {
  browser: ThreeDsBrowserData;
  billingAddress: ThreeDsAddressPayload;
  /** Ausente aqui: o BFF copia o de cobrança (ASS-08). */
  shippingAddress?: ThreeDsAddressPayload;
  holderName: string;
  email: string;
  phone: {
    countryCode: string;
    number: string;
  };
  /**
   * FIN-7470 (Rede/REDIRECT, C3DS-RD-14): rota de retorno para o provider
   * devolver o comprador após o ACS. A base é o domínio do próprio checkout
   * (DECISÃO 2). Ausente quando não há origin (fora do navegador) — o BFF trata
   * o campo como opcional.
   */
  urls?: {
    success: string;
    failure: string;
  };
}

/** Endereço como o formulário do checkout entrega (nomes de campo do formik). */
export interface BillingAddressFormValues {
  cep?: string;
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city?: string;
  state?: string;
}

interface BuildThreeDsPayloadParams {
  address?: BillingAddressFormValues;
  holderName?: string;
  email?: string;
  phone?: string;
  /** Injetável para teste; por padrão lê do navegador. */
  browser?: ThreeDsBrowserData;
  /** Injetável para teste; por padrão lê de `window.location.origin`. */
  origin?: string;
}

const DEFAULT_COUNTRY = 'BR';
const DEFAULT_COUNTRY_CALLING_CODE = '55';

/**
 * Tela de acompanhamento para onde a Rede devolve o comprador. success e failure
 * apontam para a mesma rota: o desfecho real é decidido por polling, não por qual
 * URL o provider abriu (a mesma rota que o checkout usa no NuPay).
 */
const PAYMENT_VERIFICATION_ROUTE = '/payment-verification';

const buildReturnUrls = (
  origin?: string
): { success: string; failure: string } | undefined => {
  const resolved =
    origin ?? (typeof window !== 'undefined' ? window.location.origin : '');

  if (!resolved) {
    return undefined;
  }

  const returnUrl = `${resolved}${PAYMENT_VERIFICATION_ROUTE}`;

  return { success: returnUrl, failure: returnUrl };
};

const onlyDigits = (value?: string): string => (value ?? '').replace(/\D/g, '');

const isFilled = (value?: string): boolean => !!value && value.trim() !== '';

/**
 * O provider exige código do país separado do número. `react-phone-number-input`
 * já é usado na coleta do telefone; quando não consegue parsear (número
 * digitado sem DDI, por exemplo), assume Brasil — que é a operação deste
 * checkout — em vez de mandar o campo vazio e derrubar o bloco na borda.
 */
export const splitPhone = (
  phone?: string
): { countryCode: string; number: string } | undefined => {
  const digits = onlyDigits(phone);

  if (!digits) {
    return undefined;
  }

  try {
    const parsed = parsePhoneNumber(phone ?? '', DEFAULT_COUNTRY);

    if (parsed?.countryCallingCode && parsed?.nationalNumber) {
      return {
        countryCode: String(parsed.countryCallingCode),
        number: String(parsed.nationalNumber),
      };
    }
  } catch {
    // Número fora do padrão não pode derrubar o pagamento aqui; cai no default.
  }

  if (digits.startsWith(DEFAULT_COUNTRY_CALLING_CODE) && digits.length > 11) {
    return {
      countryCode: DEFAULT_COUNTRY_CALLING_CODE,
      number: digits.slice(DEFAULT_COUNTRY_CALLING_CODE.length),
    };
  }

  return { countryCode: DEFAULT_COUNTRY_CALLING_CODE, number: digits };
};

/**
 * FIN-7470 (C3DS-09): monta o bloco `threeDs` do POST de pagamento.
 *
 * O BFF valida o bloco **inteiro** na borda e recusa payload pela metade — por
 * isso aqui é tudo ou nada: faltando qualquer dado obrigatório, devolve
 * `undefined` e a compra segue sem o bloco, em vez de ser recusada por um
 * campo vazio.
 *
 * `ip` e `acceptHeader` não entram: quem os preenche é o servidor.
 * `shippingAddress` não entra: produto digital não tem entrega, e o BFF copia
 * o endereço de cobrança (ASS-08).
 */
export const buildThreeDsPayload = ({
  address,
  holderName,
  email,
  phone,
  browser,
  origin,
}: BuildThreeDsPayloadParams): ThreeDsPayload | undefined => {
  const zipCode = onlyDigits(address?.cep);
  const splittedPhone = splitPhone(phone);

  const isComplete =
    isFilled(address?.street) &&
    isFilled(address?.number) &&
    isFilled(address?.district) &&
    isFilled(address?.city) &&
    isFilled(address?.state) &&
    isFilled(zipCode) &&
    isFilled(holderName) &&
    isFilled(email) &&
    !!splittedPhone;

  if (!isComplete) {
    return undefined;
  }

  const urls = buildReturnUrls(origin);

  return {
    browser: browser ?? collectBrowserData(),
    billingAddress: {
      street: (address?.street as string).trim(),
      number: (address?.number as string).trim(),
      ...(isFilled(address?.complement) && {
        complement: (address?.complement as string).trim(),
      }),
      neighborhood: (address?.district as string).trim(),
      city: (address?.city as string).trim(),
      state: (address?.state as string).trim(),
      zipCode,
      country: DEFAULT_COUNTRY,
    },
    holderName: (holderName as string).trim(),
    email: (email as string).trim(),
    phone: splittedPhone as { countryCode: string; number: string },
    ...(urls && { urls }),
  };
};
